// The name a picture or a file is saved under, with the extension of what it
// really is (client-10: chat pictures and deck pictures are JPEGs, and a
// JPEG saved as "luna-123.png" opens as a broken PNG in some apps). The
// type comes from the file's mime type — a data: URL's own, an attachment's
// mime_type, a fetched Blob's type — else the name's extension stays, else
// the caller's fallback.
const MIME_EXT = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/svg+xml": "svg",
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "text/plain": "txt",
  "text/csv": "csv",
  "application/zip": "zip",
};
// The same type under another name (a JPEG may be "x.jpeg").
const SAME_EXT = { jpeg: "jpg", jpg: "jpg" };
const KNOWN_EXT = new Set([...Object.values(MIME_EXT), "jpeg"]);

// "image/jpeg" -> "jpg"; "" for a type without a known extension.
export function extForMime(mime) {
  const type = typeof mime === "string" ? mime.split(";")[0].trim().toLowerCase() : "";
  return MIME_EXT[type] || "";
}

// The mime type of a data: URL ("data:image/jpeg;base64,..." -> "image/jpeg"),
// or "" for any other address (a blob: URL says nothing about its type).
export function dataUrlMime(url) {
  const m = typeof url === "string" ? /^data:([\w.+-]+\/[\w.+-]+)[;,]/i.exec(url) : null;
  return m ? m[1].toLowerCase() : "";
}

// name ("luna-gorsel", "gorsel.png", "Bütçe") saved as `mime` ("image/jpeg"):
// "luna-gorsel.jpg", "gorsel.jpg", "Bütçe.xlsx". A known extension that
// doesn't match the type is replaced; a dot that isn't one ("Plan v1.2") is
// kept and the right extension added. fallbackExt: when the type says
// nothing (an older server, "application/octet-stream").
export function downloadName(name, mime, fallbackExt = "") {
  const base = (typeof name === "string" ? name.trim() : "") || "luna-dosya";
  const m = /\.([A-Za-z0-9]{1,5})$/.exec(base);
  const have = m ? m[1].toLowerCase() : "";
  const ext = extForMime(mime) || (KNOWN_EXT.has(have) ? "" : fallbackExt);
  if (!ext) return base;
  if (have && (have === ext || SAME_EXT[have] === ext)) return base;
  if (have && KNOWN_EXT.has(have)) return `${base.slice(0, -m[0].length)}.${ext}`;
  return `${base}.${ext}`;
}

// A gallery page added to what is listed, each item once, in the order
// given (a file deleted or made meanwhile shifts the server's pages):
// components/ImageGalleryPanel.jsx, DocGeneratorPanel.jsx.
export function mergeById(have, more) {
  const list = Array.isArray(have) ? have : [];
  const seen = new Set(list.map((x) => x?.id));
  return [...list, ...(Array.isArray(more) ? more : []).filter((x) => !seen.has(x?.id))];
}

// The blob: URLs made for photos you sent (their preview in your bubble,
// pages/Luna.jsx) that no message on screen shows any more — the chat was
// switched or cleared — and can be let go (URL.revokeObjectURL): each one
// holds the whole photo in memory until then (audit client-06).
export function staleObjectUrls(urls, messages) {
  const shown = new Set((Array.isArray(messages) ? messages : []).map((m) => m?.imageUrl).filter(Boolean));
  return [...(urls || [])].filter((u) => !shown.has(u));
}
